/** ***********
*
* <role-selection.component.ts v1.0>
*
* Copyright (c) 2023 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*  NG6C740 [thomas.pereira.external@airbus.com]
*
* All rights reserved.
**************/

import { RoleLevel, RoleLevelEnum, ROLES_I18N } from 'src/app/models/user-right';
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { TranslateService } from '@ngx-translate/core';
import { xor } from 'lodash';

interface Role {
  value: RoleLevel;
  text: string;
  disabled?: boolean;
}

@Component({
    selector: 'app-role-selection',
    templateUrl: './role-selection.component.html',
    styleUrls: ['./role-selection.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RoleSelectionComponent implements OnChanges {

  @Input() role: RoleLevel = RoleLevelEnum.basic;
  @Input() disabled = false;
  @Input() editableRoleLimit!: RoleLevel;
  @Input() availableRoles: RoleLevel[] = Object.values(RoleLevelEnum);
  @Input() color: 'primary'|'accent'|'default' = 'default';
  @Output() selectedRoleChange = new EventEmitter<RoleLevel>();

  public canModify = true;
  public roles: Role[] = [...ROLES_I18N];

  constructor(
    public translate: TranslateService,
    private _cd: ChangeDetectorRef,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.availableRoles) {
      if (xor(changes.availableRoles.previousValue, changes.availableRoles.currentValue).length) {
        this.roles = [...ROLES_I18N].filter(
          role => changes.availableRoles.currentValue.includes(role.value)
        );
        this._cd.markForCheck();
      }
    }
    if (changes.color) {
      if (changes.color.previousValue !== changes.color.currentValue) {
        this._cd.markForCheck();
      }
    }
    if (changes.role) {
      if (changes.role.previousValue !== changes.role.currentValue) {
        this._cd.markForCheck();
      }
    }
    if (changes.disabled) {
      if (changes.disabled.previousValue !== changes.disabled.currentValue) {
        this._cd.markForCheck();
      }
    }
    if (changes.editableRoleLimit) {
      if (changes.editableRoleLimit.previousValue !== changes.editableRoleLimit.currentValue) {
        if (changes.editableRoleLimit.currentValue) {
          const editableRoleLimitIndex = this.roles.findIndex(role => role.value === this.editableRoleLimit);
          const selectedRoleIndex = this.roles.findIndex(role => role.value === this.role);
          this.canModify = editableRoleLimitIndex >= selectedRoleIndex;
        } else {
          this.canModify = true;
        }
        this._cd.markForCheck();
      }
    }
  }

  public onSelectionChange(selection: MatButtonToggleChange) {
    this.selectedRoleChange.emit(selection.value);
  }
}
